import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import {
  INVESTOR_DETAIL_FIELDS,
  formatCrmDate,
  humanize,
  isCrmReferrerViewEnabled,
  kycBadge,
  noteAuthorLabel,
  presentFields,
} from "../lib/crmInvestors";
import { ArrowLeft, Activity, MessageSquare, RefreshCw, Send, UserRound } from "lucide-react";

/**
 * InvestorDetailPage — one investor from the referrer's book on the
 * EchoSense CRM (STARSEED-CRM-011).
 *
 * Renders EXACTLY what the persona-redacted API returns: identity
 * basics, pipeline stage, capital band (a range, never a dollar
 * amount), the KYC status flag, last-contact, and the caller's own
 * notes + activities. Hidden fields arrive as absent keys and render
 * nothing (lib/crmInvestors.js rules 1 + 2 — no scaffolds, no
 * placeholders). Note creation writes through proxy → bridge → CRM.
 */
const InvestorDetailPage = () => {
  const { partyId } = useParams();
  const { user } = useAuth();
  const [party, setParty] = useState(null);
  const [notes, setNotes] = useState([]);
  const [activities, setActivities] = useState([]);
  // 'loading' | 'ready' | 'not-found' | 'unavailable'
  const [state, setState] = useState("loading");
  const [noteBody, setNoteBody] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const fetchAll = useCallback(async () => {
    setState("loading");
    try {
      const detailRes = await api.get(`/me/crm/investors/${partyId}`);
      setParty(detailRes.data?.party || null);
      const [notesRes, activitiesRes] = await Promise.allSettled([
        api.get(`/me/crm/investors/${partyId}/notes`),
        api.get(`/me/crm/investors/${partyId}/activities`),
      ]);
      setNotes(notesRes.status === "fulfilled" ? notesRes.value.data?.notes || [] : []);
      setActivities(
        activitiesRes.status === "fulfilled" ? activitiesRes.value.data?.activities || [] : [],
      );
      setState("ready");
    } catch (error) {
      // 404 = absent OR outside the caller's book — indistinguishable by
      // design (contract §3 "entirely absent"); 403/5xx = unavailable.
      setState(error?.response?.status === 404 ? "not-found" : "unavailable");
    }
  }, [partyId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const submitNote = async () => {
    const body = noteBody.trim();
    if (!body) return;
    setSavingNote(true);
    try {
      // Referrers author referrer-tier notes; the PLATFORM enforces
      // authoring tiers (the proxy carries no visibility logic).
      await api.post(`/me/crm/investors/${partyId}/notes`, {
        visibilityTier: "referrer",
        body,
      });
      setNoteBody("");
      toast.success("Note added");
      const notesRes = await api.get(`/me/crm/investors/${partyId}/notes`);
      setNotes(notesRes.data?.notes || []);
    } catch {
      toast.error("Could not add the note");
    } finally {
      setSavingNote(false);
    }
  };

  // Belt-and-braces behind the route gate (fail-closed launch flag).
  if (!isCrmReferrerViewEnabled(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  const fields = presentFields(INVESTOR_DETAIL_FIELDS, party);

  return (
    <DashboardLayout title="Investor" subtitle="EchoSense CRM investor record">
      <div className="mb-6">
        <Link
          to="/my-investors"
          className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-gold transition-colors"
          data-testid="crm-back-to-list"
        >
          <ArrowLeft size={16} />
          Back to My Investors
        </Link>
      </div>

      {state === "loading" && (
        <div className="animate-pulse space-y-6">
          <div className="h-48 bg-surface rounded" data-testid="crm-detail-loading" />
        </div>
      )}

      {state === "not-found" && (
        <Card className="bg-surface border-white/5" data-testid="crm-detail-not-found">
          <CardContent className="p-8 text-center text-gray-400">
            <UserRound className="mx-auto mb-3 opacity-50" size={32} />
            <p>This investor isn't in your book.</p>
          </CardContent>
        </Card>
      )}

      {state === "unavailable" && (
        <Card className="bg-surface border-white/5" data-testid="crm-detail-unavailable">
          <CardContent className="p-8 text-center text-gray-400">
            <RefreshCw className="mx-auto mb-3 opacity-50" size={32} />
            <p>EchoSense CRM is temporarily unavailable.</p>
            <button type="button" onClick={fetchAll} className="mt-3 text-gold hover:underline text-sm">
              Try again
            </button>
          </CardContent>
        </Card>
      )}

      {state === "ready" && party && (
        <div className="space-y-6">
          {/* Identity + matrix fields — presence-driven, no scaffolds */}
          <Card className="bg-surface border-white/5" data-testid="crm-investor-detail">
            <CardHeader>
              <CardTitle className="text-white font-serif flex items-center gap-3">
                {party.name || "Investor"}
                {"kycStatus" in party && kycBadge(party.kycStatus) && (
                  <Badge className={kycBadge(party.kycStatus).cls}>
                    {kycBadge(party.kycStatus).label}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>EchoSense CRM record</CardDescription>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                {fields
                  .filter((f) => f.key !== "kycStatus") /* shown as the header badge */
                  .map((f) => {
                    const value = f.format ? f.format(party[f.key]) : party[f.key];
                    return (
                      <div key={f.key} data-testid={`crm-field-${f.key}`}>
                        <dt className="text-xs uppercase tracking-wider text-gray-500">{f.label}</dt>
                        <dd className="text-white mt-1">
                          {value ?? <span className="text-gray-600">—</span>}
                        </dd>
                      </div>
                    );
                  })}
              </dl>
            </CardContent>
          </Card>

          {/* My notes — own notes only for referrers (server-redacted) */}
          <Card className="bg-surface border-white/5" data-testid="crm-notes-card">
            <CardHeader>
              <CardTitle className="text-white font-serif flex items-center gap-2">
                <MessageSquare className="text-gold" size={20} />
                Notes
              </CardTitle>
              <CardDescription>Your notes on this investor</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 mb-6">
                {notes.length === 0 && (
                  <p className="text-gray-500 text-sm">No notes yet.</p>
                )}
                {notes.map((note) => (
                  <div
                    key={note.id}
                    className="p-3 rounded bg-obsidian border border-white/5"
                    data-testid={`crm-note-${note.id}`}
                  >
                    <p className="text-white text-sm whitespace-pre-wrap">{note.body}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      {noteAuthorLabel(note, user?.id) && (
                        <span className="text-gold mr-2">{noteAuthorLabel(note, user?.id)}</span>
                      )}
                      {formatCrmDate(note.createdAt)}
                    </p>
                  </div>
                ))}
              </div>
              <div className="space-y-3">
                <Textarea
                  value={noteBody}
                  onChange={(e) => setNoteBody(e.target.value)}
                  placeholder="Add a note about this investor…"
                  className="bg-obsidian border-white/10 text-platinum"
                  data-testid="crm-note-input"
                />
                <Button
                  onClick={submitNote}
                  disabled={savingNote || !noteBody.trim()}
                  variant="outline"
                  className="border-white/10 hover:bg-gold/10 hover:text-gold hover:border-gold/50"
                  data-testid="crm-note-submit"
                >
                  <Send className="mr-2" size={16} />
                  {savingNote ? "Saving…" : "Add note"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* My activity — actor-scoped server-side for referrers */}
          <Card className="bg-surface border-white/5" data-testid="crm-activities-card">
            <CardHeader>
              <CardTitle className="text-white font-serif flex items-center gap-2">
                <Activity className="text-gold" size={20} />
                Activity
              </CardTitle>
              <CardDescription>Your recorded touchpoints</CardDescription>
            </CardHeader>
            <CardContent>
              {activities.length === 0 ? (
                <p className="text-gray-500 text-sm">No activity recorded yet.</p>
              ) : (
                <div className="space-y-3">
                  {activities.map((activity) => (
                    <div
                      key={activity.id}
                      className="flex items-center justify-between p-3 rounded bg-obsidian border border-white/5"
                      data-testid={`crm-activity-${activity.id}`}
                    >
                      <span className="text-white text-sm">{humanize(activity.activityType)}</span>
                      <span className="text-gray-500 text-xs">{formatCrmDate(activity.occurredAt)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </DashboardLayout>
  );
};

export default InvestorDetailPage;
